import { i18next } from 'core';
import { Loading } from 'ui';
import * as coreEntities from 'core/store/index';
import { getLanguageByTenant } from 'core/utils/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getInterfaceLanguageIds, getLanguageWithBundle, importLanguageResources } from '../utils/languages';

/**
 * TranslationsProvider
 *
 * Provider que carga las traducciones según el tenant del usuario.
 * Extrae la lógica de carga de traducciones desde los Routes para centralizarla a nivel de App.
 *
 * Funcionalidad:
 * - Lee el tenant, el idioma del usuario y los idiomas de interfaz del tenant desde Redux
 * - Coerciona el idioma del usuario a los idiomas de interfaz disponibles del tenant
 * - Carga los recursos de traducciones basándose en el tenant usando importLanguageResources
 * - Inyecta las traducciones en i18next
 * - Cambia el idioma activo en i18next y Redux
 * - Muestra un loading mientras las traducciones se cargan
 *
 * Sobre el momento de la coerción: la lista de idiomas de interfaz llega por `GET /langs`
 * después de autenticar, así que al arrancar todavía no se conoce. El provider no bloquea el
 * arranque esperándola (la petición la lanzan los Routes, que son hijos de este provider):
 * resuelve con lo que sabe y reacciona cuando la lista llega, aplicando el cambio solo si el
 * idioma resultante es distinto del ya aplicado. Así, en los tenants donde el idioma del usuario
 * sí está disponible (el caso habitual) no hay ni recarga de recursos ni parpadeo de idioma.
 */

interface TranslationsProviderProps {
  children: React.ReactNode;
}

/**
 * Inyecta en i18next los bundles del tenant para todos los idiomas disponibles.
 */
function addTranslationResources(tenant: unknown, language: string): void {
  const languageKeys = importLanguageResources(tenant, language);

  Object.keys(languageKeys).forEach(function (lang) {
    // Verificar que existan traducciones para este idioma
    if (languageKeys[lang] && Array.isArray(languageKeys[lang])) {
      languageKeys[lang].forEach((langScope: any) => {
        Object.keys(langScope).forEach(function (langScopeNs) {
          i18next.addResources(lang, langScopeNs, langScope[langScopeNs]);
        });
      });
    } else {
      console.warn(`[TranslationsProvider] No translations found for language "${lang}", skipping.`);
    }
  });
}

function TranslationsProvider({ children }: TranslationsProviderProps): JSX.Element {
  const dispatch = useDispatch();
  const [translationsLoaded, setTranslationsLoaded] = useState<boolean>(false);
  // Par tenant/idioma ya aplicado, para no reaplicar traducciones cuando llegan los idiomas
  // del tenant y la coerción no cambia el idioma activo.
  const appliedLanguageKeyRef = useRef<string | null>(null);

  // Selectores de Redux
  const tenant = useSelector((state: any) => coreEntities.organization.selectors.getTenant(state));
  const userLangId = useSelector((state: any) => state.auth?.user?.lang_id);
  const tenantLangs = useSelector((state: any) => coreEntities.langs.selectors.getLangs(state));

  // Idiomas de interfaz disponibles del tenant con bundle de traducción. Vacío mientras
  // `GET /langs` no ha respondido: en ese caso no se coerciona nada.
  const availableLanguages = useMemo<string[]>(() => getInterfaceLanguageIds(tenantLangs), [tenantLangs]);

  useEffect(() => {
    if (!tenant?.guid) {
      dispatch(coreEntities.organization.actions.getOrganization());
      return;
    }

    try {
      // 1. Resolver el idioma: forzado por tenant, idioma del usuario o idioma del tenant,
      //    coercionado a los idiomas de interfaz disponibles cuando ya se conocen
      const resolvedLanguage = getLanguageByTenant(tenant?.guid, userLangId, tenant?.lang_id, availableLanguages);

      // 2. Garantizar que el idioma resuelto tiene bundle, para no caer al fallbackLng
      const defaultLanguage = getLanguageWithBundle(resolvedLanguage, tenant?.lang_id);

      // 3. Aplicar traducciones e idioma solo si cambia el par tenant/idioma
      const languageKey = `${tenant?.guid}|${defaultLanguage}`;
      if (appliedLanguageKeyRef.current !== languageKey) {
        appliedLanguageKeyRef.current = languageKey;
        addTranslationResources(tenant, defaultLanguage);
        i18next.changeLanguage(defaultLanguage);
        dispatch(coreEntities.i18n.actions.setLanguage(defaultLanguage));
      }

      // 4. Marcar las traducciones como cargadas
      setTranslationsLoaded(true);
    } catch (error) {
      console.error('Error loading translations:', error);
      // En caso de error, igualmente marcamos como cargado para no bloquear la app
      setTranslationsLoaded(true);
    }
  }, [tenant?.guid, tenant?.lang_id, userLangId, availableLanguages, dispatch]);

  // Si no hay tenant aún, no mostramos nada (esperamos a que se cargue)
  if (!tenant?.guid) {
    return <Loading />;
  }

  // Si las traducciones no están cargadas aún, mostramos loading
  if (!translationsLoaded) {
    return <Loading />;
  }

  // Renderizamos los children cuando las traducciones están cargadas
  return <>{children}</>;
}

export default TranslationsProvider;
